import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Star, 
  Download, 
  Shield, 
  Clock, 
  Key,
  Terminal,
  Play,
  Copy,
  Check,
  Zap,
  ExternalLink,
  Loader2,
  Activity,
  Code,
  FileJson,
  BarChart3,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Framework icons and colors
const frameworks = [
  { id: 'python', name: 'Python', color: 'bg-blue-500' },
  { id: 'nodejs', name: 'Node.js', color: 'bg-green-500' },
  { id: 'langchain', name: 'LangChain', color: 'bg-purple-500' },
  { id: 'crewai', name: 'CrewAI', color: 'bg-pink-500' },
  { id: 'openai', name: 'OpenAI', color: 'bg-emerald-500' },
  { id: 'autogpt', name: 'AutoGPT', color: 'bg-amber-500' },
  { id: 'flowise', name: 'Flowise', color: 'bg-cyan-500' },
];

const ConnectorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [connector, setConnector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [deploymentType, setDeploymentType] = useState('docker');
  const [deploying, setDeploying] = useState(false);
  const [copied, setCopied] = useState(null);
  
  // New states for enhanced features
  const [installSnippets, setInstallSnippets] = useState(null);
  const [selectedFramework, setSelectedFramework] = useState('python');
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [pcsSchema, setPcsSchema] = useState(null);

  useEffect(() => {
    const fetchConnector = async () => {
      try {
        const [connectorRes, snippetsRes, healthRes, metricsRes, pcsRes] = await Promise.all([
          axios.get(`${API}/connectors/${id}`),
          axios.get(`${API}/connectors/${id}/install-snippets`).catch(() => ({ data: null })),
          axios.get(`${API}/health/${id}`).catch(() => ({ data: null })),
          axios.get(`${API}/connectors/${id}/metrics`).catch(() => ({ data: null })),
          axios.get(`${API}/connectors/${id}/pcs`).catch(() => ({ data: null })),
        ]);
        
        setConnector(connectorRes.data);
        setInstallSnippets(snippetsRes.data?.snippets);
        setHealth(healthRes.data);
        setMetrics(metricsRes.data);
        setPcsSchema(pcsRes.data);
      } catch (error) {
        console.error('Error fetching connector:', error);
        toast.error('Connector not found');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    fetchConnector();
  }, [id, navigate]);

  const handleDeploy = async () => {
    if (!user) {
      toast.error('Please sign in to deploy connectors');
      return;
    }
    
    setDeploying(true);
    try {
      await axios.post(`${API}/deployments`, {
        connector_id: id,
        deployment_type: deploymentType,
      });
      toast.success('Connector deployed successfully!');
      setDeployModalOpen(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deployment failed');
    } finally {
      setDeploying(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const getDeploymentCode = () => {
    if (!connector) return '';
    const name = connector.service_name.toLowerCase().replace(/\s+/g, '-');
    
    switch (deploymentType) {
      case 'docker':
        return `docker pull pipeyard/${name}:latest\ndocker run -d -e API_KEY=your_key pipeyard/${name}`;
      case 'npm':
        return `npm install @pipeyard/${name}`;
      case 'pip':
        return `pip install pipeyard-${name}`;
      case 'api_key':
        return `# API Endpoint\nPOST https://api.pipeyard.io/v1/mcp/${name}`;
      case 'mcp_endpoint':
        return `mcp://pipeyard.io/${name}`;
      default:
        return '';
    }
  };

  const getHealthBadge = () => {
    if (!health) return null;
    const statusColors = {
      online: 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50',
      degraded: 'bg-amber-950/30 text-amber-400 border-amber-900/50',
      offline: 'bg-red-950/30 text-red-400 border-red-900/50',
    };
    const statusIcons = {
      online: <CheckCircle className="w-3.5 h-3.5" />,
      degraded: <AlertTriangle className="w-3.5 h-3.5" />,
      offline: <Activity className="w-3.5 h-3.5" />,
    };
    return (
      <Badge className={`${statusColors[health.status]} flex items-center gap-1 capitalize`}>
        {statusIcons[health.status]}
        {health.status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!connector) return null;

  const currentSnippet = installSnippets?.[selectedFramework];

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link 
          to="/marketplace" 
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
          data-testid="back-to-marketplace"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Marketplace
        </Link>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 mb-12">
          <div className="flex items-start gap-6 flex-1">
            <div className="w-20 h-20 rounded-xl bg-secondary flex items-center justify-center overflow-hidden flex-shrink-0">
              {connector.icon_url ? (
                <img src={connector.icon_url} alt={connector.name} className="w-full h-full object-cover" />
              ) : (
                <Zap className="w-10 h-10 text-primary" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                  {connector.name}
                </h1>
                {connector.is_verified && (
                  <Badge className="bg-accent/20 text-accent border-accent/30">
                    <Shield className="w-3.5 h-3.5 mr-1" />
                    Verified
                  </Badge>
                )}
                {getHealthBadge()}
              </div>
              <p className="text-lg text-slate-400 mb-4">{connector.description}</p>
              
              {/* Framework Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-sm text-slate-500">Works with:</span>
                {frameworks.map((fw) => (
                  <Badge 
                    key={fw.id}
                    variant="outline" 
                    className="border-slate-700 text-slate-400 text-xs"
                  >
                    <span className={`w-2 h-2 rounded-full ${fw.color} mr-1.5`} />
                    {fw.name}
                  </Badge>
                ))}
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <Badge variant="outline" className="border-slate-700 text-slate-300">
                  {connector.industry}
                </Badge>
                <Badge variant="outline" className="border-slate-700 text-slate-300">
                  {connector.category}
                </Badge>
                <div className="flex items-center gap-1 text-slate-400">
                  <Download className="w-4 h-4" />
                  {connector.deployment_count?.toLocaleString()} deployments
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <span className="text-slate-300">{connector.rating?.toFixed(1)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 lg:w-64">
            <Button 
              size="lg" 
              className="glow-primary bg-primary hover:bg-primary/90"
              onClick={() => setDeployModalOpen(true)}
              data-testid="deploy-btn"
            >
              <Zap className="w-5 h-5 mr-2" />
              Deploy Connector
            </Button>
            <Link to={`/sandbox/${connector.id}`}>
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full border-slate-700 text-slate-300 hover:bg-secondary"
                data-testid="try-sandbox-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Try in Sandbox
              </Button>
            </Link>
          </div>
        </div>

        {/* Health & Metrics Bar */}
        {(health || metrics) && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            {health && (
              <>
                <div className="bg-card border border-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Uptime</span>
                    <span className="text-white font-mono">{health.uptime_percentage}%</span>
                  </div>
                  <Progress value={health.uptime_percentage} className="h-2 bg-slate-800" />
                </div>
                <div className="bg-card border border-white/5 rounded-xl p-4">
                  <span className="text-sm text-slate-400">Avg Response</span>
                  <div className="text-2xl font-bold text-white mt-1">{health.avg_response_time_ms}ms</div>
                </div>
              </>
            )}
            {metrics && (
              <>
                <div className="bg-card border border-white/5 rounded-xl p-4">
                  <span className="text-sm text-slate-400">API Calls (24h)</span>
                  <div className="text-2xl font-bold text-white mt-1">{metrics.api_calls_24h?.toLocaleString()}</div>
                </div>
                <div className="bg-card border border-white/5 rounded-xl p-4">
                  <span className="text-sm text-slate-400">Success Rate</span>
                  <div className="text-2xl font-bold text-emerald-400 mt-1">{(metrics.success_rate * 100).toFixed(1)}%</div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="install" className="space-y-8">
          <TabsList className="bg-secondary">
            <TabsTrigger value="install">Install in Agent</TabsTrigger>
            <TabsTrigger value="actions">Actions</TabsTrigger>
            <TabsTrigger value="docs">Documentation</TabsTrigger>
            <TabsTrigger value="schema">API Schema</TabsTrigger>
          </TabsList>

          {/* Install in Agent Tab */}
          <TabsContent value="install" className="space-y-6">
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <h2 className="font-heading text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-primary" />
                Install in Your Agent
              </h2>
              
              {/* Framework Selector */}
              <div className="flex flex-wrap gap-2 mb-6">
                {frameworks.map((fw) => (
                  <button
                    key={fw.id}
                    onClick={() => setSelectedFramework(fw.id)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      selectedFramework === fw.id
                        ? 'bg-primary/20 border-primary/50 text-primary'
                        : 'bg-secondary border-border text-slate-400 hover:text-white hover:border-white/20'
                    }`}
                    data-testid={`framework-${fw.id}`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${fw.color} mr-2`} />
                    {fw.name}
                  </button>
                ))}
              </div>

              {currentSnippet && (
                <div className="space-y-4">
                  {/* Install Command */}
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Install</label>
                    <div className="bg-slate-950 rounded-lg p-4 flex items-center justify-between">
                      <code className="text-emerald-400 font-mono text-sm">
                        {currentSnippet.install_command}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(currentSnippet.install_command, 'install')}
                        className="text-slate-500 hover:text-white"
                      >
                        {copied === 'install' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Code Snippet */}
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Usage Example</label>
                    <div className="terminal rounded-lg overflow-hidden">
                      <div className="terminal-header px-4 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500/50" />
                          <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                          <div className="w-3 h-3 rounded-full bg-green-500/50" />
                          <span className="text-xs text-slate-500 ml-2 font-mono">
                            {selectedFramework === 'python' ? 'main.py' : 
                             selectedFramework === 'nodejs' ? 'index.js' : 'agent.py'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(currentSnippet.code_snippet, 'code')}
                          className="text-slate-500 hover:text-white"
                        >
                          {copied === 'code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <pre className="p-4 overflow-x-auto text-sm text-blue-300 font-mono leading-relaxed">
                        {currentSnippet.code_snippet}
                      </pre>
                    </div>
                  </div>

                  {/* Environment Variables */}
                  {currentSnippet.env_variables?.length > 0 && (
                    <div>
                      <label className="text-sm text-slate-400 mb-2 block">Required Environment Variables</label>
                      <div className="flex flex-wrap gap-2">
                        {currentSnippet.env_variables.map((env, i) => (
                          <code key={i} className="px-3 py-1.5 bg-slate-950 rounded text-sm text-amber-300 font-mono">
                            {env}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dependencies */}
                  {currentSnippet.dependencies?.length > 0 && (
                    <div>
                      <label className="text-sm text-slate-400 mb-2 block">Dependencies</label>
                      <div className="flex flex-wrap gap-2">
                        {currentSnippet.dependencies.map((dep, i) => (
                          <Badge key={i} variant="outline" className="border-slate-700 text-slate-300 font-mono">
                            {dep}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Actions Tab */}
          <TabsContent value="actions" className="space-y-4">
            {connector.supported_actions?.map((action, index) => (
              <div 
                key={index}
                className="bg-card border border-white/5 rounded-xl p-6 hover:border-white/10 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <code className="text-lg font-mono text-primary">{action.name}</code>
                    <p className="text-slate-400 mt-1">{action.description}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(action.example_prompt, `action-${index}`)}
                    className="text-slate-400 hover:text-white"
                  >
                    {copied === `action-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {action.inputs?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-300 mb-2">Inputs</h4>
                    <div className="flex flex-wrap gap-2">
                      {action.inputs.map((input, i) => (
                        <code key={i} className="px-2 py-1 bg-slate-950 rounded text-xs text-slate-300 font-mono">
                          {input.name}: {input.type}
                          {input.required && <span className="text-destructive">*</span>}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-slate-950/50 rounded-lg p-4 border border-slate-800">
                  <div className="text-xs text-slate-500 mb-2">Example prompt</div>
                  <p className="text-slate-300 font-mono text-sm">"{action.example_prompt}"</p>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Documentation Tab */}
          <TabsContent value="docs">
            <div className="bg-card border border-white/5 rounded-xl p-8">
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-slate-300 font-mono text-sm leading-relaxed">
                  {connector.documentation}
                </pre>
              </div>
            </div>
          </TabsContent>

          {/* API Schema Tab */}
          <TabsContent value="schema">
            <div className="bg-card border border-white/5 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileJson className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-white">Pipeyard Connector Standard (PCS)</h3>
                </div>
                {pcsSchema && (
                  <Badge variant="outline" className="border-slate-700 text-slate-400">
                    v{pcsSchema.pcs_version}
                  </Badge>
                )}
              </div>
              <div className="p-4 max-h-[600px] overflow-auto">
                <pre className="text-sm text-slate-300 font-mono">
                  {pcsSchema ? JSON.stringify(pcsSchema, null, 2) : 'Loading...'}
                </pre>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Deploy Modal */}
      <Dialog open={deployModalOpen} onOpenChange={setDeployModalOpen}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Deploy {connector.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose your preferred deployment method
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <Select value={deploymentType} onValueChange={setDeploymentType}>
              <SelectTrigger className="bg-secondary border-border" data-testid="deployment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="docker">Docker Container</SelectItem>
                <SelectItem value="npm">npm Package</SelectItem>
                <SelectItem value="pip">pip Package</SelectItem>
                <SelectItem value="api_key">API Key</SelectItem>
                <SelectItem value="mcp_endpoint">MCP Endpoint</SelectItem>
              </SelectContent>
            </Select>

            <div className="terminal rounded-lg overflow-hidden">
              <div className="terminal-header px-4 py-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/50" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                <div className="w-3 h-3 rounded-full bg-green-500/50" />
                <span className="text-xs text-slate-500 ml-2 font-mono">deployment</span>
              </div>
              <div className="p-4 relative">
                <pre className="text-sm text-blue-300 font-mono overflow-x-auto">
                  {getDeploymentCode()}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 text-slate-500 hover:text-white"
                  onClick={() => copyToClipboard(getDeploymentCode(), 'deploy-code')}
                >
                  {copied === 'deploy-code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-slate-700"
                onClick={() => setDeployModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 glow-primary bg-primary hover:bg-primary/90"
                onClick={handleDeploy}
                disabled={deploying}
                data-testid="confirm-deploy-btn"
              >
                {deploying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Deploy Now
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectorPage;
